import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@components/common/sheet"
import useTranslator from "@hooks/use-translator"
import { cn } from "@pma/utils/helpers"

// ---------------------------------------------------------------------------
// DrillDownSheet — reusable slide-in panel for drill-down views
// ---------------------------------------------------------------------------

const widthMap = {
  narrow: "max-w-[400px]",
  medium: "max-w-[560px]",
  wide: "max-w-[720px]",
} as const

export interface DrillDownSheetProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  loading?: boolean
  empty?: boolean
  emptyMessage?: string
  width?: "narrow" | "medium" | "wide"
  children: React.ReactNode
  footer?: React.ReactNode
}

export function DrillDownSheet({
  open,
  onClose,
  title,
  subtitle,
  loading = false,
  empty = false,
  emptyMessage,
  width = "medium",
  children,
  footer,
}: DrillDownSheetProps) {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"
  const side = isHebrew ? "left" : "right"

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <SheetContent
        side={side}
        className={cn(
          "flex flex-col w-full sm:w-full",
          widthMap[width],
          "p-0",
        )}
      >
        {/* Title bar */}
        <SheetHeader className={cn("border-b px-5 py-4 shrink-0", isHebrew ? "rtl" : "ltr")}>
          <SheetTitle className={cn("text-base font-semibold leading-tight", isHebrew ? "text-right" : "text-left")}>
            {title}
          </SheetTitle>
          {subtitle && (
            <SheetDescription className={cn("text-xs mt-0.5", isHebrew ? "text-right" : "text-left")}>
              {subtitle}
            </SheetDescription>
          )}
        </SheetHeader>

        {/* Scrollable content */}
        <div
          className="flex-1 overflow-y-auto px-5 py-4"
          dir={isHebrew ? "rtl" : "ltr"}
        >
          {loading ? (
            <DrillDownSkeleton />
          ) : empty ? (
            <DrillDownEmpty message={emptyMessage} />
          ) : (
            children
          )}
        </div>

        {/* Optional footer */}
        {footer && (
          <SheetFooter className="border-t px-5 py-3 shrink-0">
            {footer}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DrillDownSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="h-4 w-32 rounded bg-gray-200" />
          <div className="h-4 w-16 rounded bg-gray-200" />
          <div className="h-4 flex-1 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function DrillDownEmpty({ message }: { message?: string }) {
  const { translate } = useTranslator()
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-12 w-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-3">
        <svg className="h-6 w-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      </div>
      <p className="text-sm text-muted-foreground">
        {message || translate("No data to display")}
      </p>
    </div>
  )
}
